import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';

import { serviceurl } from 'src/app/constant/constant';

const ACCESS_KEY = 'wt_access';
const REFRESH_KEY = 'wt_refresh';
/** The token key used before (per-tab sessionStorage); read once to migrate. */
const LEGACY_KEY = 'token';
/** Preview mode's fake token (see preview/preview.util.ts) never expires. */
const PREVIEW_TOKEN = 'preview-token';
/** Renew a little before the real expiry, so a request never races it. */
const RENEW_EARLY_SECONDS = 60;

/**
 * The app's login tokens (from the backend at sign-in):
 *  - access token: sent with every request to our backend, lasts 30 minutes;
 *  - refresh token: swaps for a new pair at Api/token/refresh/, lasts 7 days
 *    and is replaced on every renewal — so a session ends after 7 days unused.
 *
 * Kept in localStorage so every tab shares them (the old sessionStorage
 * token was lost in new tabs).
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  /** HttpClient without interceptors: the renewal call must not trigger itself. */
  private readonly rawHttp: HttpClient;
  private refreshing$: Observable<string> | null = null;

  constructor(backend: HttpBackend) {
    this.rawHttp = new HttpClient(backend);
    this.migrateLegacyToken();
  }

  // ---- reading / writing ---------------------------------------------------

  get accessToken(): string | null {
    return this.read(ACCESS_KEY) || this.readSession(LEGACY_KEY);
  }

  get refreshToken(): string | null {
    return this.read(REFRESH_KEY);
  }

  /** Called after sign-in and after every renewal. */
  setTokens(access: string | null | undefined, refresh?: string | null): void {
    if (!access) return;
    this.write(ACCESS_KEY, access);
    if (refresh) this.write(REFRESH_KEY, refresh);
  }

  /**
   * Signing out: asks the server to cancel the refresh token (so a copy of
   * it can't keep renewing the session), then forgets both tokens. Fire and
   * forget — signing out must never wait on or fail because of the network.
   */
  revoke(): void {
    const refresh = this.refreshToken;
    if (refresh && !this.isExpired(refresh)) {
      this.rawHttp.post(`${serviceurl}Api/token/logout/`, { refresh }).subscribe({ error: () => {} });
    }
    this.clear();
  }

  clear(): void {
    this.remove(ACCESS_KEY);
    this.remove(REFRESH_KEY);
    try {
      sessionStorage.removeItem(LEGACY_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  // ---- state -----------------------------------------------------------------

  /** True while a request can still be signed: a fresh access token, or a refresh token to get one. */
  get hasSession(): boolean {
    const access = this.accessToken;
    if (access === PREVIEW_TOKEN) return true;
    return (!!access && !this.isExpired(access)) || this.canRefresh;
  }

  get canRefresh(): boolean {
    const refresh = this.refreshToken;
    return !!refresh && !this.isExpired(refresh);
  }

  /** The access token expires within RENEW_EARLY_SECONDS (or is unreadable). */
  get accessNeedsRenewal(): boolean {
    const access = this.accessToken;
    if (!access) return this.canRefresh;
    if (access === PREVIEW_TOKEN) return false;
    return this.isExpired(access, RENEW_EARLY_SECONDS);
  }

  // ---- renewal ---------------------------------------------------------------

  /**
   * Swaps the refresh token for a new pair and emits the new access token.
   * Concurrent callers share one request. Errors (no / expired / rejected
   * refresh token) clear the tokens and re-throw.
   */
  renew(): Observable<string> {
    if (this.refreshing$) return this.refreshing$;
    const refresh = this.refreshToken;
    if (!refresh || this.isExpired(refresh)) {
      return throwError(() => new Error('session-ended'));
    }
    this.refreshing$ = this.rawHttp
      .post<{ access: string; refresh?: string }>(`${serviceurl}Api/token/refresh/`, { refresh })
      .pipe(
        map((res) => {
          this.setTokens(res.access, res.refresh);
          return res.access;
        }),
        // Renewal tokens work once and all tabs share them: if another tab
        // renewed at the same moment, ours is refused as "already used" —
        // give that tab a moment to save its new pair and use it, instead
        // of signing the person out everywhere.
        catchError((err) =>
          timer(1500).pipe(
            switchMap(() => {
              const access = this.accessToken;
              if (this.refreshToken !== refresh && access && !this.isExpired(access)) return of(access);
              this.clear();
              return throwError(() => err);
            }),
          ),
        ),
        finalize(() => (this.refreshing$ = null)),
        shareReplay(1),
      );
    return this.refreshing$;
  }

  /** The access token to send now, renewing it first when it's about to expire. */
  validAccessToken(): Observable<string | null> {
    if (!this.accessNeedsRenewal) return of(this.accessToken);
    if (!this.canRefresh) return of(this.isExpired(this.accessToken) ? null : this.accessToken);
    return this.renew().pipe(catchError(() => of(null)));
  }

  // ---- helpers -----------------------------------------------------------------

  /** Seconds-since-epoch expiry from a JWT, or null if it can't be read. */
  private expiry(token: string | null): number | null {
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const exp = JSON.parse(json)?.exp;
      return typeof exp === 'number' ? exp : null;
    } catch {
      return null;
    }
  }

  private isExpired(token: string | null, earlySeconds = 0): boolean {
    const exp = this.expiry(token);
    if (exp == null) return true;
    return exp - earlySeconds <= Date.now() / 1000;
  }

  /** Moves a token from the old per-tab key so signed-in users stay signed in. */
  private migrateLegacyToken(): void {
    const legacy = this.readSession(LEGACY_KEY);
    if (legacy && legacy !== PREVIEW_TOKEN && !this.read(ACCESS_KEY)) {
      this.write(ACCESS_KEY, legacy);
      try {
        sessionStorage.removeItem(LEGACY_KEY);
      } catch {
        /* storage unavailable */
      }
    }
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private readSession(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* storage unavailable: this tab stays signed in until reload */
    }
  }

  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage unavailable */
    }
  }
}
